import { z } from 'zod'
import { APP_SPONSORSHIP_STATUSES } from './billing'

// Spec PWA-015 — apadrinhar uma nova crianca (catalogo, reserva, pagamento simulado) e marcos da crianca.
// Perfil e marcos vem do app da OSP (PARTNER-003): so o que a FR-007 de la permite. Saidas com dado de crianca .strict().

export const ADOPT_GENDERS = ['female', 'male', 'other'] as const

// Listas fechadas do perfil (copia do partner-app/src/contract.ts, PARTNER-003 D-01); rotulos no i18n
export const PERSONALITY_TRAITS = [
    'cheerful', 'calm', 'affectionate', 'communicative', 'creative', 'curious', 'outgoing', 'shy', 'active', 'studious', 'playful', 'observant',
] as const
export const SCHOOL_STAGES = ['not_attending', 'daycare', 'preschool', 'elementary_early', 'elementary_late', 'high_school'] as const
export const SCHOOL_NETWORKS = ['public', 'private', 'not_applicable'] as const
export const LIVES_WITH = [
    'mother', 'father', 'grandmother', 'grandfather', 'stepmother', 'stepfather', 'aunt_uncle', 'other_relatives', 'non_relative_guardian',
] as const
export const PARENTS_STATUS = ['together', 'separated', 'mother_deceased', 'father_deceased', 'both_deceased', 'not_informed'] as const
export const INCOME_COVERS = ['yes', 'partly', 'no'] as const
export const HOUSING_TENURE = ['owned', 'rented', 'lent', 'other'] as const
export const HOUSING_MATERIAL = ['masonry', 'wood', 'wattle_daub', 'mixed', 'other'] as const
export const HOUSE_FEATURES = ['bathroom', 'electricity', 'piped_water', 'treated_water', 'sewage', 'internet'] as const
export const CHILD_NEEDS = [
    'education', 'educational_support', 'health', 'nutrition', 'protection', 'family_community', 'culture', 'sports_leisure',
] as const
export const MILESTONE_KINDS = ['school', 'sports', 'arts', 'personal', 'other'] as const

export const sponsorChildProfileSchema = z.object({
    personality: z.array(z.enum(PERSONALITY_TRAITS)).max(3).optional(),
    likes: z.string().max(200).optional(),
    dream: z.string().max(120).optional(),
    pet: z.string().max(80).optional(),
    chores: z.string().max(160).optional(),
    schoolStage: z.enum(SCHOOL_STAGES).optional(),
    schoolNetwork: z.enum(SCHOOL_NETWORKS).optional(),
    favoriteSubject: z.string().max(60).optional(),
    livesWith: z.array(z.enum(LIVES_WITH)).optional(),
    siblings: z.number().int().min(0).max(15).optional(),
    parentsStatus: z.enum(PARENTS_STATUS).optional(),
    livelihood: z.string().max(120).optional(),
    incomeCovers: z.enum(INCOME_COVERS).optional(),
    housingTenure: z.enum(HOUSING_TENURE).optional(),
    housingMaterial: z.enum(HOUSING_MATERIAL).optional(),
    houseFeatures: z.array(z.enum(HOUSE_FEATURES)).optional(),
    needs: z.array(z.enum(CHILD_NEEDS)).max(4).optional(),
    about: z.string().max(1000).optional(),
}).strict()
export type SponsorChildProfile = z.infer<typeof sponsorChildProfileSchema>

const photoSchema = z.object({ photoUrl: z.string().url(), expiresAt: z.string().datetime() }).strict()

export const sponsorMilestoneSchema = z.object({
    id: z.string().uuid(),
    kind: z.enum(MILESTONE_KINDS),
    month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/),
    title: z.string().min(1).max(80),
    body: z.string().min(1).max(500),
    photo: photoSchema.nullable(),
    approvedAt: z.string().datetime(),
}).strict()
export type SponsorMilestone = z.infer<typeof sponsorMilestoneSchema>

// ─── Catalogo (US-001, US-002) ───────────────────────────────

export const CATALOG_AGE_BANDS = ['0-5', '6-10', '11-14', '15-17'] as const
export const sponsorCatalogQuerySchema = z.object({
    gender: z.enum(ADOPT_GENDERS).optional(),
    age: z.enum(CATALOG_AGE_BANDS).optional(),
    state: z.string().regex(/^[A-Z]{2}$/).optional(),
    cursor: z.string().uuid().optional(),
}).strict()
export type SponsorCatalogQuery = z.infer<typeof sponsorCatalogQuerySchema>

export const sponsorCatalogCardSchema = z.object({
    id: z.string().uuid(),
    firstName: z.string().min(1).max(40),
    ageYears: z.number().int().min(0).max(17).nullable(),
    gender: z.enum(ADOPT_GENDERS).nullable(),
    state: z.string().length(2).nullable(),
    osp: z.object({ name: z.string().min(1).max(120) }).strict(),
    needs: z.array(z.enum(CHILD_NEEDS)).max(4),
    photo: photoSchema.nullable(),
}).strict()
export type SponsorCatalogCard = z.infer<typeof sponsorCatalogCardSchema>

export const sponsorCatalogPageSchema = z.object({
    items: z.array(sponsorCatalogCardSchema),
    total: z.number().int().min(0),
    next: z.string().uuid().nullable(),
    states: z.array(z.string().length(2)),
    /** Pagamento ligado (PWA-015 FR-004); desligado, a tela nao mostra "Quero apadrinhar". */
    canAdopt: z.boolean(),
}).strict()
export type SponsorCatalogPage = z.infer<typeof sponsorCatalogPageSchema>

export const sponsorCatalogChildSchema = sponsorCatalogCardSchema.extend({
    profile: sponsorChildProfileSchema,
    milestones: z.array(sponsorMilestoneSchema).max(50),
    canAdopt: z.boolean(),
}).strict()
export type SponsorCatalogChild = z.infer<typeof sponsorCatalogChildSchema>

export const sponsorReservationSchema = z.object({ reservationId: z.string().uuid(), expiresAt: z.string().datetime() }).strict()
export type SponsorReservation = z.infer<typeof sponsorReservationSchema>

// ─── Pagamento simulado (US-003, D-08/D-09) — formato do JSON da ficha do Live Concert ───────────────

export const SPONSORSHIP_AMOUNTS_CENTS = [9000, 12000, 16000] as const
export const SPONSORSHIP_MIN_CENTS = 9000
export const SPONSORSHIP_MAX_CENTS = 100000
export const SPONSORSHIP_DUE_DAYS = [5, 10, 15, 20, 25] as const
export const CARD_BRANDS = ['visa', 'mastercard', 'elo', 'amex', 'hipercard', 'other'] as const

export const sponsorshipCheckoutSchema = z.object({
    childId: z.string().uuid(),
    reservationId: z.string().uuid(),
    sponsorship: z.object({
        monthlyAmountCents: z.number().int().min(SPONSORSHIP_MIN_CENTS).max(SPONSORSHIP_MAX_CENTS).multipleOf(100),
        dueDay: z.union([z.literal(5), z.literal(10), z.literal(15), z.literal(20), z.literal(25)]),
    }).strict(),
    payment: z.object({
        method: z.literal('credit_card'),
        creditCard: z.object({
            // Token do gateway (simulado: sim_<uuid>); numero e CVV nunca chegam ao servidor
            token: z.string().min(8).max(200),
            brand: z.enum(CARD_BRANDS),
            holderName: z.string().trim().min(2).max(60),
            last4: z.string().regex(/^\d{4}$/),
            expiryMonth: z.number().int().min(1).max(12),
            expiryYear: z.number().int().min(2024).max(2100),
            // PWA-022 C#2: VerifyCard que o aparelho recebeu da Braspag (so com BRASPAG_VERIFY=sop)
            verify: z.object({ status: z.enum(['0', '1', '99']), code: z.string().max(10).optional() }).strict().optional(),
        }).strict(),
    }).strict(),
    acceptTerms: z.literal(true),
}).strict()
export type SponsorshipCheckout = z.infer<typeof sponsorshipCheckoutSchema>

export const sponsorshipResultSchema = z.object({
    protocol: z.string().min(8).max(40),
    childRef: z.string().regex(/^[A-Za-z0-9_-]{22}$/),
    childFirstName: z.string().min(1).max(40),
    childGender: z.enum(ADOPT_GENDERS).nullable(),
    monthlyAmountCents: z.number().int(),
    dueDay: z.number().int(),
    card: z.object({ brand: z.enum(CARD_BRANDS), last4: z.string().regex(/^\d{4}$/) }).strict(),
    simulated: z.boolean(),
}).strict()
export type SponsorshipResult = z.infer<typeof sponsorshipResultSchema>

const cardSummarySchema = z.object({ brand: z.enum(CARD_BRANDS), last4: z.string().regex(/^\d{4}$/) }).strict()
const nextChargeSchema = z.object({ on: z.string().date(), amountCents: z.number().int() }).strict()

/** Minhas doacoes (C#7; PWA-019: situacao, proxima cobranca e os encerrados). */
export const sponsorAppSponsorshipSchema = z.object({
    protocol: z.string().min(8).max(40),
    /** Ficha da crianca (so enquanto ativo) — a ficha acha o proprio apadrinhamento por ele. */
    childRef: z.string().regex(/^[A-Za-z0-9_-]{22}$/).nullable(),
    childFirstName: z.string().min(1).max(40),
    monthlyAmountCents: z.number().int(),
    dueDay: z.number().int(),
    card: cardSummarySchema,
    since: z.string().date(),
    simulated: z.boolean(),
    status: z.enum(APP_SPONSORSHIP_STATUSES),
    nextCharge: nextChargeSchema.nullable(),
    canceledAt: z.string().datetime().nullable(),
}).strict()
export type SponsorAppSponsorship = z.infer<typeof sponsorAppSponsorshipSchema>

// ─── PWA-019: gerenciar o apadrinhamento feito pelo app ───────────────

export const APP_CHARGE_STATUSES = ['processing', 'paid', 'declined', 'retrying', 'discarded'] as const
export const APP_SPONSORSHIP_EVENTS = ['created', 'card_changed', 'amount_changed', 'due_day_changed', 'canceled'] as const
export const CANCEL_REASONS = ['financial', 'other_child', 'follow_up', 'other'] as const

export const sponsorAppChargeSchema = z.object({
    referenceMonth: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/),
    dueOn: z.string().date(),
    /** Ultima resposta do gateway (null enquanto processa). */
    chargedAt: z.string().datetime().nullable(),
    amountCents: z.number().int(),
    card: cardSummarySchema,
    status: z.enum(APP_CHARGE_STATUSES),
    /** Mensagem do gateway quando recusada (ex.: "Saldo insuficiente"). */
    message: z.string().max(200).nullable(),
    nextAttemptOn: z.string().date().nullable(),
}).strict()
export type SponsorAppCharge = z.infer<typeof sponsorAppChargeSchema>

const snapshotSchema = z.object({
    amountCents: z.number().int().optional(),
    dueDay: z.number().int().optional(),
    card: cardSummarySchema.optional(),
    reason: z.enum(CANCEL_REASONS).nullable().optional(),
}).strict()

export const sponsorAppEventSchema = z.object({
    type: z.enum(APP_SPONSORSHIP_EVENTS),
    at: z.string().datetime(),
    before: snapshotSchema.nullable(),
    after: snapshotSchema.nullable(),
}).strict()
export type SponsorAppEvent = z.infer<typeof sponsorAppEventSchema>

/** O que mostrar no aviso do topo (US-001 aceite 3). */
export const sponsorAppIssueSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('charge_declined'), referenceMonth: z.string().regex(/^\d{4}-\d{2}$/), dueOn: z.string().date(), message: z.string().max(200).nullable(), nextAttemptOn: z.string().date().nullable() }).strict(),
    z.object({ kind: z.literal('card_expired'), expiry: z.string().regex(/^\d{2}\/\d{4}$/) }).strict(),
    z.object({ kind: z.literal('card_expiring'), expiry: z.string().regex(/^\d{2}\/\d{4}$/) }).strict(),
])
export type SponsorAppIssue = z.infer<typeof sponsorAppIssueSchema>

export const sponsorAppSponsorshipDetailSchema = z.object({
    protocol: z.string().min(8).max(40),
    /** Ficha da crianca (so enquanto ativo). */
    childRef: z.string().regex(/^[A-Za-z0-9_-]{22}$/).nullable(),
    childFirstName: z.string().min(1).max(40),
    childGender: z.enum(ADOPT_GENDERS).nullable(),
    ospName: z.string().max(120).nullable(),
    since: z.string().date(),
    monthlyAmountCents: z.number().int(),
    dueDay: z.number().int(),
    card: cardSummarySchema.extend({ expiry: z.string().regex(/^\d{2}\/\d{4}$/) }).strict(),
    status: z.enum(APP_SPONSORSHIP_STATUSES),
    issue: sponsorAppIssueSchema.nullable(),
    nextCharge: nextChargeSchema.nullable(),
    /** Primeiro dia que pode ter cobranca (para o "a partir de" da tela, com src/contract/billing.ts). */
    billingStartsOn: z.string().date(),
    chargedMonths: z.array(z.string().regex(/^\d{4}-\d{2}$/)),
    canceledAt: z.string().datetime().nullable(),
    cancelReason: z.enum(CANCEL_REASONS).nullable(),
    charges: z.array(sponsorAppChargeSchema),
    events: z.array(sponsorAppEventSchema),
    simulated: z.boolean(),
    /** false na impersonacao (so leitura) e quando o pagamento esta desligado. */
    canEdit: z.boolean(),
}).strict()
export type SponsorAppSponsorshipDetail = z.infer<typeof sponsorAppSponsorshipDetailSchema>

const creditCardInputSchema = sponsorshipCheckoutSchema.shape.payment.shape.creditCard

export const sponsorAppCardChangeSchema = z.object({ card: creditCardInputSchema, requestKey: z.string().uuid() }).strict()
export type SponsorAppCardChange = z.infer<typeof sponsorAppCardChangeSchema>

export const sponsorAppCardChangeResultSchema = z.object({
    sponsorship: sponsorAppSponsorshipDetailSchema,
    /** Cobranca em aberto tentada de novo com o cartao novo (US-002 aceite 2). */
    retriedCharge: sponsorAppChargeSchema.nullable(),
}).strict()
export type SponsorAppCardChangeResult = z.infer<typeof sponsorAppCardChangeResultSchema>

export const sponsorAppPlanChangeSchema = z.object({
    monthlyAmountCents: sponsorshipCheckoutSchema.shape.sponsorship.shape.monthlyAmountCents.optional(),
    dueDay: sponsorshipCheckoutSchema.shape.sponsorship.shape.dueDay.optional(),
}).strict().refine(v => v.monthlyAmountCents !== undefined || v.dueDay !== undefined)
export type SponsorAppPlanChange = z.infer<typeof sponsorAppPlanChangeSchema>

export const sponsorAppCancelSchema = z.object({ reason: z.enum(CANCEL_REASONS).optional() }).strict()
export type SponsorAppCancel = z.infer<typeof sponsorAppCancelSchema>
